import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getTickerPerformance } from '../store/analytics/thunks';
import { getTradesPage } from '../store/trades/thunks';
import { getStudyNoteTickers } from '../store/study/thunks';
import { useNavigate } from 'react-router-dom';
import { Chart } from 'react-google-charts';
import {
  Box,
  Flex,
  HStack,
  Heading,
  Text,
  SimpleGrid,
  Center,
  VStack,
  Badge,
  useColorMode,
} from '@chakra-ui/react';
import getSymbolFromCurrency from 'currency-symbol-map';
import AnalyticsSkeleton from './AnalyticsSkeleton';
import NoteIndicator from './study/NoteIndicator';

import '../styles/profile.css';

export default function TickerAnalytics({ user }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const tickerPerformance = useSelector((state) => state.analytics.tickerPerformance);
  const loading = useSelector((state) => state.ui.loading);
  const preferences = useSelector((state) => state.user.preferences);
  const noteTickersLoaded = useSelector((state) => Boolean(state.study?.noteTickersLoaded));

  const currencySymbol = getSymbolFromCurrency(preferences?.preferred_currency || 'USD') || '$';

  useEffect(() => {
    dispatch(getTickerPerformance());
  }, [dispatch]);

  useEffect(() => {
    if (!noteTickersLoaded) {
      dispatch(getStudyNoteTickers());
    }
  }, [dispatch, noteTickersLoaded]);

  const handleTickerClick = async (tickerName) => {
    const filters = {
      page: 1,
      numrows: 100,
      ticker_name: tickerName,
    };
    window.localStorage.setItem('SummaryFilters', JSON.stringify(filters));
    await dispatch(getTradesPage({ filters }));
    navigate('/summary');
  };

  const barChartData = [
    ['Ticker', 'Total P&L', { role: 'style' }],
    ...tickerPerformance.map((t) => [
      t.ticker_name,
      t.total_pnl,
      t.total_pnl >= 0 ? '#48BB78' : '#FC8181',
    ]),
  ];

  const barChartOptions = {
    title: 'P&L by Ticker',
    chartArea: { width: '70%', height: '80%' },
    height: '100%',
    width: '100%',
    backgroundColor: colorMode === 'light' ? '#FDFDFD' : '#1A202C',
    titleTextStyle: {
      color: colorMode === 'light' ? '#636363' : '#E2E8F0',
      fontName: 'Open Sans',
      bold: true,
      fontSize: 16,
    },
    hAxis: {
      title: `P&L (${currencySymbol})`,
      titleTextStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0' },
      textStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0' },
    },
    vAxis: {
      textStyle: { color: colorMode === 'light' ? '#636363' : '#E2E8F0' },
    },
    legend: 'none',
  };

  if (loading && tickerPerformance.length === 0) {
    return (
      <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
        <Box flex="auto" overflowX="auto" overflowY="auto">
          <VStack spacing={4} p={4} align="stretch" w="full">
            <AnalyticsSkeleton heatmapColumns={{ base: 2, sm: 3, md: 4, lg: 6 }} heatmapCount={8} />
          </VStack>
        </Box>
      </Flex>
    );
  }

  if (tickerPerformance.length === 0) {
    return (
      <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
        <Box flex="auto" overflowX="auto" overflowY="auto">
          <Center h="60vh">
            <Text color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
              No trade data yet. Log some trades to see ticker analytics.
            </Text>
          </Center>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" height="100vh" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
      <Box flex="auto" overflowX="auto" overflowY="auto">
        <VStack spacing={4} p={4} align="stretch" w="full">
          <Heading className="page-heading" fontSize="md" color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
            Ticker Analytics
          </Heading>

          {/* Heatmap */}
          <Box>
            <Heading size="sm" mb={3} color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
              Performance Heatmap
            </Heading>
            <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing={3}>
              {tickerPerformance.map((t) => {
                const isPositive = t.total_pnl >= 0;
                return (
                  <Box
                    key={t.ticker_name}
                    p={3}
                    rounded="md"
                    cursor="pointer"
                    bg={isPositive ? 'green.400' : 'red.400'}
                    color="white"
                    _hover={{ opacity: 0.85, transform: 'scale(1.03)', transition: '0.15s' }}
                    onClick={() => handleTickerClick(t.ticker_name)}
                    textAlign="center"
                  >
                    <HStack justify="center" spacing={1}>
                      <Text fontWeight="bold" fontSize="sm">
                        {t.ticker_name}
                      </Text>
                      <NoteIndicator ticker={t.ticker_name} />
                    </HStack>
                    <Text fontSize="xs">
                      {isPositive ? '+' : ''}{currencySymbol}{t.total_pnl.toFixed(2)}
                    </Text>
                    <Text fontSize="xs" opacity={0.9}>
                      {t.win_rate !== null ? `${t.win_rate}% WR` : 'No closed trades'}
                    </Text>
                    <Text fontSize="xs" opacity={0.85}>
                      {t.num_trades} trade{t.num_trades !== 1 ? 's' : ''}
                    </Text>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>

          {/* Bar Chart */}
          <Box
            bg={colorMode === 'light' ? 'white' : 'gray.700'}
            rounded="lg"
            boxShadow="sm"
            p={4}
            h="400px"
          >
            <Chart
              chartType="BarChart"
              width="100%"
              height="100%"
              data={barChartData}
              options={barChartOptions}
            />
          </Box>

          {/* Table */}
          <Box overflowX="auto">
            <Heading size="sm" mb={3} color={colorMode === 'light' ? 'gray.600' : 'gray.200'}>
              Breakdown
            </Heading>
            <Box
              as="table"
              w="100%"
              bg={colorMode === 'light' ? 'white' : 'gray.700'}
              rounded="lg"
              boxShadow="sm"
              overflow="hidden"
              fontSize="sm"
            >
              <Box as="thead" bg={colorMode === 'light' ? 'gray.100' : 'gray.600'}>
                <Box as="tr">
                  {['Ticker', 'Trades', 'Wins', 'Losses', 'Win Rate', 'Total P&L', 'Avg P&L'].map((h) => (
                    <Box
                      as="th"
                      key={h}
                      p={3}
                      textAlign="left"
                      fontWeight="semibold"
                      color={colorMode === 'light' ? 'gray.600' : 'gray.200'}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box as="tbody">
                {tickerPerformance.map((t, i) => (
                  <Box
                    as="tr"
                    key={t.ticker_name}
                    bg={i % 2 === 0 ? 'transparent' : colorMode === 'light' ? 'gray.50' : 'gray.750'}
                    cursor="pointer"
                    _hover={{ bg: colorMode === 'light' ? 'blue.50' : 'gray.600' }}
                    onClick={() => handleTickerClick(t.ticker_name)}
                  >
                    <Box as="td" p={3} fontWeight="bold">
                      <HStack spacing={1}>
                        <Text>{t.ticker_name}</Text>
                        <NoteIndicator ticker={t.ticker_name} />
                      </HStack>
                    </Box>
                    <Box as="td" p={3}>{t.num_trades}</Box>
                    <Box as="td" p={3} color="green.400">{t.num_wins}</Box>
                    <Box as="td" p={3} color="red.400">{t.num_losses}</Box>
                    <Box as="td" p={3}>
                      {t.win_rate !== null ? (
                        <Badge colorScheme={t.win_rate >= 50 ? 'green' : 'red'}>
                          {t.win_rate}%
                        </Badge>
                      ) : '—'}
                    </Box>
                    <Box as="td" p={3} color={t.total_pnl >= 0 ? 'green.400' : 'red.400'} fontWeight="semibold">
                      {t.total_pnl >= 0 ? '+' : ''}{currencySymbol}{t.total_pnl.toFixed(2)}
                    </Box>
                    <Box as="td" p={3} color={t.avg_pnl !== null && t.avg_pnl >= 0 ? 'green.400' : 'red.400'}>
                      {t.avg_pnl !== null ? `${t.avg_pnl >= 0 ? '+' : ''}${currencySymbol}${t.avg_pnl.toFixed(2)}` : '—'}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </VStack>
      </Box>
    </Flex>
  );
}
